/**
 * Navigation Components
 * Reusable navigation components using centralized styles
 */

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { NAVIGATION_STYLES, combineClasses } from "./styleCompositions";
import { EFFECTS, LAYOUT, COLORS } from "./styleConstants";

// Dropdown icon component
export const DropdownIcon = () => (
  <svg
    className={combineClasses(
      "ml-1",
      LAYOUT.height4,
      LAYOUT.height4,
      EFFECTS.iconHidden,
      EFFECTS.iconVisible,
      EFFECTS.transitionOpacity,
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Mobile dropdown toggle icon component
export const MobileDropdownIcon = ({ isOpen }) => (
  <svg
    className={combineClasses(
      LAYOUT.marginLeftAuto,
      LAYOUT.height5,
      LAYOUT.height5,
      EFFECTS.transitionTransform,
      COLORS.groupHover,
    )}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d={isOpen ? "M19 9l-7 7-7-7" : "M9 5l7 7-7 7"}
      className={isOpen ? EFFECTS.transform180 : ""}
    />
  </svg>
);

// Main Navigation Styles Hook
export const useNavigationStyles = () => {
  const getStyles = (variant) => {
    return NAVIGATION_STYLES[variant] || "";
  };

  const getDropdownVisibility = (isOpen) => {
    return isOpen ? EFFECTS.visible : EFFECTS.hidden;
  };

  const getMobileDropdownVisibility = (isOpen) => {
    return isOpen ? EFFECTS.mobileVisible : EFFECTS.mobileHidden;
  };

  return {
    getStyles,
    getDropdownVisibility,
    getMobileDropdownVisibility,
    styles: NAVIGATION_STYLES,
  };
};

// Navigation Link Component
export const NavigationLink = ({
  to,
  children,
  variant = "desktop-link",
  onClick,
  className = "",
  ...props
}) => {
  const { getStyles } = useNavigationStyles();
  const location = useLocation();

  const handleClick = (e) => {
    const currentPath = location.pathname;
    const targetPath = to;

    const currentBasePath = "/" + currentPath.split("/")[1];
    const targetBasePath = "/" + targetPath.split("/")[1];

    if (currentPath === targetPath || currentBasePath === targetBasePath) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Link
      to={to}
      className={`${getStyles(variant)} ${className}`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
};

// Navigation Button Component (for dropdowns)
export const NavigationButton = ({
  children,
  variant = "desktop-button",
  onClick,
  onMouseEnter,
  onMouseLeave,
  className = "",
  showIcon = true,
  iconComponent,
  ...props
}) => {
  const { getStyles } = useNavigationStyles();

  return (
    <button
      className={`${getStyles(variant)} ${className}`}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      {children}
      {showIcon && (iconComponent || <DropdownIcon />)}
    </button>
  );
};

// Navigation Dropdown Container Component
export const NavigationDropdown = ({
  isOpen,
  children,
  variant = "dropdown-container",
  onMouseEnter,
  onMouseLeave,
  className = "",
  ...props
}) => {
  const { getStyles, getDropdownVisibility } = useNavigationStyles();

  return (
    <div
      className={`${getStyles(variant)} ${getDropdownVisibility(isOpen)} ${className}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      {children}
    </div>
  );
};
